import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SinglePopoverMenu from './SinglePopoverMenu'
import MultiPopoverMenu from './MultiPopoverMenu'
import SelectLabel from './SelectLabel'

function useOptionsFilter (searchText, options, pinnedOptions) {
  return useMemo(() => {
    const filteredPinned = (pinnedOptions || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))
    const filteredOptions = (options || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))

    return [filteredOptions, filteredPinned]
  }, [searchText, options, pinnedOptions])
}

function ScopeSelect ({
  options,
  pinnedOptions,
  currentlySelectedValues,
  onSelect,
  isLoading,
  multiSelect,
  className,
  style,
  children
}) {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [filteredOptions, filteredPinnedOptions] = useOptionsFilter(searchText, options, pinnedOptions)

  const handleToggle = useCallback(() => {
    setOpen(p => !p)
    setSearchText('')
  }, [setOpen, setSearchText])

  const handleSelect = useCallback((scope) => {
    onSelect(scope)
    setOpen(false)
  }, [onSelect, setOpen])

  return (
    <>
      <SelectLabel
        ref={anchorRef}
        open={open}
        onClick={handleToggle}
        style={style}
        className={className}
        isLoading={isLoading}
      >
        {children}
      </SelectLabel>
      {multiSelect ? (
        <MultiPopoverMenu
          anchorEl={anchorRef.current}
          open={open}
          setOpen={setOpen}
          options={options}
          filteredOptions={filteredOptions}
          pinnedOptions={pinnedOptions}
          filteredPinnedOptions={filteredPinnedOptions}
          onSearchChange={setSearchText}
          onSelect={handleSelect}
          defaultFilterValues={currentlySelectedValues}
        />
      ) : (
        <SinglePopoverMenu
          anchorEl={anchorRef.current}
          open={open}
          setOpen={setOpen}
          options={filteredOptions}
          pinnedOptions={filteredPinnedOptions}
          onSearchChange={setSearchText}
          onSelect={handleSelect}
        />
      )}
    </>
  )
}

ScopeSelect.propTypes = {
  options: PropTypes.array,
  pinnedOptions: PropTypes.array,
  /** The currently-selected filter values */
  currentlySelectedValues: PropTypes.arrayOf(PropTypes.number),
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  multiSelect: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}

ScopeSelect.defaultProps = {
  multiSelect: true
}

export default ScopeSelect
